/**
 * Event submission form — two modes:
 *   • "Event Organizer" — full organizer submission (contact info, accessibility, etc.)
 *   • "Promote an Event" — shorter form for members who spotted an event and want to
 *     surface it. Photos optional, most detail comes from the event's own website.
 *
 * Non-admin/unverified submissions land in the moderation queue; verified users
 * publish immediately (see backend `submission_status_for`).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, X, MapPin, Link as LinkIcon, Megaphone, Users, ClipboardList, Save, Trash2 } from "lucide-react";
import SmartTextInput from "@/components/SmartTextInput";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const DEFAULT_FORM = {
  title: "", short_description: "", detailed_description: "",
  country: "", state: "", city: "", neighborhood: "", venue: "",
  street_address: "", postal_code: "", latitude: "", longitude: "",
  organizer_name: "", organization: "",
  website: "", youtube_url: "", meetup_url: "", eventbrite_url: "", other_links: "",
  email: "", phone: "",
  start_date: "", start_time: "", end_date: "", end_time: "", time_zone: "",
  recurring: false, recurrence_pattern: "",
  admission: "Free", ticket_price: "",
  registration_required: false, registration_url: "",
  family_friendly: true, pets_allowed: false, accessibility: "",
  parking: "", public_transport: "", age_restrictions: "",
  items_to_bring: "", weather_notes: "", safety_instructions: "",
  tags: "",
};

const DRAFT_KEY = "wofy_event_draft_v1";

export default function EventSubmit() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialMode = params.get("mode") === "promote" ? "promote" : "organizer";
  const [mode, setMode] = useState(initialMode);
  const [taxonomy, setTaxonomy] = useState({});
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [f, setF] = useState(DEFAULT_FORM);
  const [coverImage, setCoverImage] = useState("");
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftInitRef = useRef(false);

  useEffect(() => {
    http.get("/events/config").then((r) => setTaxonomy(r.data.taxonomy || {})).catch(() => {});
  }, []);

  // Prefill city from `?city=` (e.g. when arriving from the LocationArticle
  // "Post here" FAB or the Community Hub empty-state CTA).
  useEffect(() => {
    const cityFromQuery = params.get("city");
    if (cityFromQuery) {
      setF((prev) => (prev.city ? prev : { ...prev, city: cityFromQuery }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Draft restore on mount ----------
  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) { draftInitRef.current = true; return; }
      const d = JSON.parse(raw);
      // Draft is user-scoped so we don't leak fields across accounts.
      if (d.user_id && d.user_id !== user.id) {
        draftInitRef.current = true;
        return;
      }
      setF({ ...DEFAULT_FORM, ...(d.form || {}) });
      if (d.category) setCategory(d.category);
      if (d.subcategory) setSubcategory(d.subcategory);
      if (d.mode) setMode(d.mode);
      if (d.cover_image) setCoverImage(d.cover_image);
      if (Array.isArray(d.images)) setImages(d.images);
      if (d.saved_at) setDraftSavedAt(d.saved_at);
      setDraftRestored(true);
    } catch { /* ignore corrupt draft */ }
    finally {
      // Delay auto-save enablement by one tick so restore doesn't immediately re-write.
      setTimeout(() => { draftInitRef.current = true; }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---------- Debounced auto-save every ~5s of idle typing ----------
  useEffect(() => {
    if (!user || !draftInitRef.current) return;
    // Only save if there's actually something to save.
    const empty =
      !category && !subcategory && !coverImage && images.length === 0 &&
      Object.values(f).every((v) => v === "" || v === false || v === true);
    if (empty) return;

    const t = setTimeout(() => {
      try {
        const payload = {
          user_id: user.id,
          mode, category, subcategory,
          form: f, cover_image: coverImage, images,
          saved_at: new Date().toISOString(),
        };
        // localStorage cap on most browsers is 5–10 MB. Skip persisting
        // huge photo payloads to avoid QuotaExceeded — we only save
        // small (≤1 MB total) media into the draft.
        const mediaBytes = (payload.cover_image?.length || 0) + payload.images.reduce((s, i) => s + (i?.length || 0), 0);
        if (mediaBytes > 1024 * 1024) {
          payload.cover_image = "";
          payload.images = [];
          payload.__media_stripped = true;
        }
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        setDraftSavedAt(payload.saved_at);
      } catch { /* quota — ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [user, mode, category, subcategory, f, coverImage, images]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setF(DEFAULT_FORM);
    setCategory(""); setSubcategory("");
    setCoverImage(""); setImages([]);
    setDraftSavedAt(null);
    setDraftRestored(false);
    toast.success("Draft discarded.");
  };

  useEffect(() => {
    // Keep URL in sync with the active tab so it's shareable.
    setParams({ mode }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (loading) return <div className="max-w-[900px] mx-auto p-16 text-center">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto p-16 text-center">
        <div className="overline">EVENTS</div>
        <h1 className="font-display text-3xl mt-3">Please sign in to submit an event.</h1>
        <button onClick={() => nav("/login?next=/events/submit")} className="wofy-btn-primary mt-6">Sign in</button>
      </div>
    );
  }

  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Cover image must be < 4 MB");
    const data = await fileToDataUrl(file);
    setCoverImage(data);
  };

  const onAdd = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (images.length >= 6) return toast.error("Max 6 images");
    if (file.size > 3 * 1024 * 1024) return toast.error("Each image must be < 3 MB");
    const data = await fileToDataUrl(file);
    setImages((prev) => [...prev, data]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Please pick a category");
    if (!f.title.trim() || f.title.length < 3) return toast.error("Title is required");
    if (!f.short_description.trim() || f.short_description.length < 10) return toast.error("Short description is required");
    if (!f.start_date) return toast.error("Start date is required");

    const totalMediaBytes = (coverImage?.length || 0) + images.reduce((s, i) => s + (i?.length || 0), 0);
    if (totalMediaBytes > 10 * 1024 * 1024) {
      return toast.error("Your photos total more than 10 MB — please compress or remove some before submitting.");
    }

    // Times are optional; default to 09:00 / 18:00 only when the user gave
    // a date but no time.  If they gave no end_date at all, backend now
    // auto-mirrors start → end so weekend/upcoming filters still catch it.
    const startIso = f.start_time ? `${f.start_date}T${f.start_time}` : `${f.start_date}T09:00`;
    const endIso = f.end_date
      ? (f.end_time ? `${f.end_date}T${f.end_time}` : `${f.end_date}T18:00`)
      : (f.end_time ? `${f.start_date}T${f.end_time}` : null);

    const otherLinks = f.other_links
      ? f.other_links.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
      : [];

    const body = {
      ...f,
      category,
      subcategory: subcategory || null,
      start_date: startIso,
      end_date: endIso,
      latitude: f.latitude ? parseFloat(f.latitude) : null,
      longitude: f.longitude ? parseFloat(f.longitude) : null,
      tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      other_links: otherLinks,
      is_promotion: mode === "promote",
      cover_image: coverImage || null,
      images,
      volunteer_opportunities: [],
      languages: [],
    };
    setSubmitting(true);
    try {
      const { data } = await http.post("/events", body);
      // Clear the local draft — form was successfully submitted.
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      toast.success(data.status === "approved" ? "Published!" : "Submitted for review — thank you!");
      nav(`/events/${data.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const bind = (k) => ({ value: f[k], onChange: (e) => setF((prev) => ({ ...prev, [k]: e.target.value })) });
  const bindBool = (k) => ({ checked: !!f[k], onChange: (e) => setF((prev) => ({ ...prev, [k]: e.target.checked })) });

  const categories = Object.keys(taxonomy);
  const subs = taxonomy[category] || [];
  const isPromote = mode === "promote";

  const inputClass = "w-full px-3 py-2 rounded-lg border bg-transparent";
  const inputStyle = { borderColor: "var(--wofy-border)" };

  return (
    <div data-testid="event-submit-page" className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
      <div className="overline mb-3">SUBMIT AN EVENT</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Add an event to Social Pulse.</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Are you organizing the event yourself, or did you spot a great community event
        you want to help publicize? Pick a mode below.
      </p>

      {/* Draft banner + save indicator */}
      {draftRestored && (
        <div
          data-testid="draft-restored-banner"
          className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3"
          style={{ background: "var(--wofy-surface)", border: "1px solid var(--wofy-border)" }}
        >
          <div className="text-xs flex items-center gap-2">
            <Save className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            <span>
              <strong>Draft restored</strong>
              {draftSavedAt && (
                <span className="opacity-70 ml-1">
                  · saved {new Date(draftSavedAt).toLocaleString()}
                </span>
              )}
              . Continue where you left off, or discard.
            </span>
          </div>
          <button
            type="button"
            onClick={clearDraft}
            data-testid="draft-discard"
            className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100"
            style={{ color: "#B23A3A" }}
          >
            <Trash2 className="w-3.5 h-3.5" /> Discard draft
          </button>
        </div>
      )}

      {/* Mode tabs */}
      <div className="mt-8 grid gap-3 md:grid-cols-2">
        <ModeCard
          testid="mode-organizer"
          active={mode === "organizer"}
          onClick={() => setMode("organizer")}
          icon={Users}
          title="I'm the organizer"
          subtitle="Full submission — contact info, accessibility, registration, extras."
        />
        <ModeCard
          testid="mode-promote"
          active={mode === "promote"}
          onClick={() => setMode("promote")}
          icon={Megaphone}
          title="Promote an event"
          subtitle="Short form — you spotted a great event and want it on Social Pulse."
        />
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6 text-sm">
        {/* Category */}
        <section className="wofy-card space-y-4">
          <div className="overline">CATEGORY</div>
          <div className="grid md:grid-cols-2 gap-4">
            <select data-testid="es-category" required value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(""); }} className={inputClass} style={inputStyle}>
              <option value="">Select category *</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="es-subcategory" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} className={inputClass} style={inputStyle} disabled={!subs.length}>
              <option value="">Subcategory (optional)</option>
              {subs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </section>

        {/* Basics */}
        <section className="wofy-card space-y-4">
          <div className="overline">BASICS</div>
          <input data-testid="es-title" required placeholder="Event title *" {...bind("title")} className={inputClass} style={inputStyle} />
          <input data-testid="es-short" required placeholder="Short description (1-2 sentences) *" {...bind("short_description")} maxLength={500} className={inputClass} style={inputStyle} />
          <SmartTextInput
            value={f.detailed_description}
            onChange={(v) => setF((prev) => ({ ...prev, detailed_description: v }))}
            rows={isPromote ? 3 : 5}
            maxLength={8000}
            placeholder="Detailed description (optional — leave blank if the info is on the event website)"
            testid="es-detailed"
          />
        </section>

        {/* Links — always shown, richer in Promote mode */}
        <section className="wofy-card space-y-4">
          <div className="overline"><LinkIcon className="w-3 h-3 inline mr-1" /> LINKS</div>
          <p className="text-xs opacity-70 -mt-1">
            {isPromote
              ? "Give us at least one link so people can find the event. The organizer's own website is best."
              : "Optional — link to your website, ticket page, or promo video."}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <input type="url" data-testid="es-website" placeholder="Website" {...bind("website")} className={inputClass} style={inputStyle} />
            <input type="url" data-testid="es-youtube" placeholder="YouTube / video link" {...bind("youtube_url")} className={inputClass} style={inputStyle} />
            <input type="url" data-testid="es-meetup" placeholder="Meetup link" {...bind("meetup_url")} className={inputClass} style={inputStyle} />
            <input type="url" data-testid="es-eventbrite" placeholder="Eventbrite link" {...bind("eventbrite_url")} className={inputClass} style={inputStyle} />
          </div>
          <textarea
            data-testid="es-other-links"
            rows={2}
            placeholder="Other links (one per line, or comma-separated)"
            {...bind("other_links")}
            className={inputClass}
            style={inputStyle}
          />
          <input data-testid="es-tags" placeholder="Tags (comma-separated)" {...bind("tags")} className={inputClass} style={inputStyle} />
        </section>

        {/* Location */}
        <section className="wofy-card space-y-4">
          <div className="overline"><MapPin className="w-3 h-3 inline mr-1" /> LOCATION</div>
          <div className="grid md:grid-cols-2 gap-4">
            <input data-testid="es-country" placeholder="Country" {...bind("country")} className={inputClass} style={inputStyle} />
            <input data-testid="es-state" placeholder="State / Province" {...bind("state")} className={inputClass} style={inputStyle} />
            <input data-testid="es-city" placeholder="City / Town" {...bind("city")} className={inputClass} style={inputStyle} />
            <input placeholder="Neighborhood" {...bind("neighborhood")} className={inputClass} style={inputStyle} />
            <input data-testid="es-venue" placeholder="Venue name" {...bind("venue")} className={inputClass} style={inputStyle} />
            <input placeholder="Postal code" {...bind("postal_code")} className={inputClass} style={inputStyle} />
            {!isPromote && (
              <>
                <input placeholder="Street address" {...bind("street_address")} className={`${inputClass} md:col-span-2`} style={inputStyle} />
                <input type="number" step="any" placeholder="Latitude (optional)" {...bind("latitude")} className={inputClass} style={inputStyle} />
                <input type="number" step="any" placeholder="Longitude (optional)" {...bind("longitude")} className={inputClass} style={inputStyle} />
              </>
            )}
          </div>
        </section>

        {/* When */}
        <section className="wofy-card space-y-4">
          <div className="overline">WHEN</div>
          <p className="text-xs opacity-70 -mt-1">
            Times are optional. If you only enter a start date, we'll use it as the end date too.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider">Start date *</label>
              <input type="date" data-testid="es-start-date" required {...bind("start_date")} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider">Start time (optional)</label>
              <input type="time" {...bind("start_time")} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider">End date (optional)</label>
              <input type="date" data-testid="es-end-date" {...bind("end_date")} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider">End time (optional)</label>
              <input type="time" {...bind("end_time")} className={inputClass} style={inputStyle} />
            </div>
            {!isPromote && (
              <input placeholder="Time zone (e.g. America/New_York)" {...bind("time_zone")} className={`${inputClass} md:col-span-2`} style={inputStyle} />
            )}
          </div>
          {!isPromote && (
            <>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" {...bindBool("recurring")} data-testid="es-recurring" /> Recurring event
              </label>
              {f.recurring && (
                <input placeholder="Recurrence pattern (e.g. Every Saturday, 8-10am)" {...bind("recurrence_pattern")} className={inputClass} style={inputStyle} />
              )}
            </>
          )}
        </section>

        {/* Photos — now positioned right after When for both modes */}
        <section className="wofy-card space-y-4">
          <div className="overline">PHOTOS <span className="opacity-60 font-normal normal-case tracking-normal ml-1">(optional)</span></div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider">Cover photo</label>
            <input type="file" accept="image/*" onChange={onCover} data-testid="es-cover-upload" className="block mt-1 text-xs" />
            {coverImage && <img src={coverImage} alt="cover" className="mt-2 w-full max-w-md rounded-xl border" />}
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider">Additional photos (up to 6)</label>
            <input type="file" accept="image/*" onChange={onAdd} className="block mt-1 text-xs" />
            {images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt="" className="w-24 h-24 rounded-lg object-cover border" />
                    <button
                      type="button"
                      onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                      className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white w-5 h-5 grid place-items-center"
                    ><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Organizer-only sections */}
        {!isPromote && (
          <>
            <section className="wofy-card space-y-4">
              <div className="overline">ORGANIZER</div>
              <div className="grid md:grid-cols-2 gap-4">
                <input placeholder="Organizer name" {...bind("organizer_name")} className={inputClass} style={inputStyle} />
                <input placeholder="Organization" {...bind("organization")} className={inputClass} style={inputStyle} />
                <input type="email" placeholder="Contact email" {...bind("email")} className={inputClass} style={inputStyle} />
                <input placeholder="Phone" {...bind("phone")} className={inputClass} style={inputStyle} />
              </div>
            </section>

            <section className="wofy-card space-y-4">
              <div className="overline">ADMISSION &amp; REGISTRATION</div>
              <div className="grid md:grid-cols-2 gap-4">
                <select {...bind("admission")} className={inputClass} style={inputStyle}>
                  <option value="Free">Free</option>
                  <option value="Paid">Paid</option>
                </select>
                <input placeholder="Ticket price (if Paid)" {...bind("ticket_price")} className={inputClass} style={inputStyle} />
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" {...bindBool("registration_required")} /> Registration required
                </label>
                <input type="url" placeholder="Registration URL" {...bind("registration_url")} className={inputClass} style={inputStyle} />
              </div>
            </section>

            <section className="wofy-card space-y-4">
              <div className="overline">ACCESSIBILITY &amp; EXTRAS</div>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" {...bindBool("family_friendly")} /> Family-friendly
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" {...bindBool("pets_allowed")} /> Pets allowed
                </label>
                <input placeholder="Accessibility notes" {...bind("accessibility")} className={inputClass} style={inputStyle} />
                <input placeholder="Age restrictions (e.g. 18+)" {...bind("age_restrictions")} className={inputClass} style={inputStyle} />
                <input placeholder="Parking notes" {...bind("parking")} className={inputClass} style={inputStyle} />
                <input placeholder="Public transport" {...bind("public_transport")} className={inputClass} style={inputStyle} />
                <input placeholder="Items to bring" {...bind("items_to_bring")} className={`${inputClass} md:col-span-2`} style={inputStyle} />
                <input placeholder="Weather notes" {...bind("weather_notes")} className={inputClass} style={inputStyle} />
                <input placeholder="Safety instructions" {...bind("safety_instructions")} className={inputClass} style={inputStyle} />
              </div>
            </section>
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          {draftSavedAt ? (
            <div className="text-[11px] font-mono opacity-60 inline-flex items-center gap-1.5" data-testid="draft-saved-indicator">
              <Save className="w-3 h-3" />
              Draft auto-saved · {new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          ) : <span />}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => nav("/events")} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="es-submit" className="wofy-btn-primary">
              {submitting
                ? "Submitting…"
                : isPromote ? "Submit for promotion" : "Submit event"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ModeCard({ testid, active, onClick, icon: Icon, title, subtitle }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="text-left rounded-xl p-4 border transition-all"
      style={{
        borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
        background: active ? "var(--wofy-surface)" : "transparent",
        boxShadow: active ? "0 0 0 2px var(--wofy-primary) inset" : "none",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full grid place-items-center shrink-0"
          style={{ background: active ? "var(--wofy-primary)" : "var(--wofy-surface)", color: active ? "#FDFBF7" : "var(--wofy-text)" }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg">{title}</div>
          <div className="text-xs opacity-70 mt-0.5">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}
